import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "react-router-dom";

import { eventsApi } from "../../api";
import CostBreakdownChart from "../../components/events/CostBreakdownChart";
import EventKpiStrip, { deriveEventKpis } from "../../components/events/EventKpiStrip";
import ScenarioForecastPanel from "../../components/events/ScenarioForecastPanel";
import TicketTierSplitPie from "../../components/events/TicketTierSplitPie";
import { KpiStripSkeleton, QueryErrorBanner } from "../../components/feedback/QueryFeedback";
import { useAuth } from "../../context/AuthContext";
import { useEventDashboard } from "../../hooks/useEventDashboard";
import { queryKeys } from "../../queryKeys";
import { workspaceNav } from "../../routes/workspaceNavigation";
import { formatINR } from "../../utils/format";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import "./EventPnlPage.css";

type Props = {
  workspace: WorkspaceId;
};

/**
 * Event P&L tab — the analytical half of the old combined dashboard.
 *
 * Layout follows the Sprint 14 wireframe: KPI strip, projected-vs-actual,
 * ticket tier split, scenario forecast, expense analysis.
 */
const EventPnlPage: React.FC<Props> = ({ workspace }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const { token } = useAuth();
  const { query, data, errorMessage } = useEventDashboard(workspace, eventId);

  // Fetched separately from the dashboard: 101 points would bloat a cached
  // payload that several other screens read, and only this tab needs them.
  const curveQuery = useQuery({
    queryKey:
      eventId && token
        ? queryKeys.events.forecastCurve(workspace, eventId)
        : queryKeys.events.all(workspace),
    queryFn: () => eventsApi.forecastCurve(eventId!, token!),
    enabled: Boolean(token && eventId),
  });

  const eventsPath = workspaceNav.events(workspace);

  if (!eventId) {
    return <Navigate to={eventsPath} replace />;
  }

  if (query.isPending) {
    return (
      <div className="app-shell-dash dashboard-page">
        <KpiStripSkeleton count={6} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="app-shell-dash dashboard-page">
        <QueryErrorBanner
          title="P&L request failed"
          message={errorMessage || "Unknown error"}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
        <Link to={eventsPath} className="app-shell-back-link">
          ← Back to events
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="app-shell-dash dashboard-page">
        <div className="eventos-empty">No P&amp;L payload for this id.</div>
      </div>
    );
  }

  const { projectedRevenue, projectedProfit, actualRevenue, actualProfit, totalCost } =
    deriveEventKpis(data);
  const costRows = data.chart_data.cost_breakdown ?? [];
  const variance = actualRevenue - projectedRevenue;

  return (
    <div className="app-shell-dash dashboard-page">
      <EventKpiStrip data={data} />

      <div className="event-pnl__grid">
        <div className="eventos-card event-pnl__projected">
          <div className="eventos-label">Projected vs actual</div>
          <table className="app-shell-table event-pnl__table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Projected</th>
                <th>Actual</th>
                <th>Variance</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Revenue</td>
                <td>{formatINR(projectedRevenue)}</td>
                <td>{formatINR(actualRevenue)}</td>
                <td className={variance >= 0 ? "event-pnl__pos" : "event-pnl__neg"}>
                  {variance >= 0 ? "+" : "−"}
                  {formatINR(Math.abs(variance))}
                </td>
              </tr>
              <tr>
                <td>Profit</td>
                <td>{formatINR(projectedProfit)}</td>
                <td>{formatINR(actualProfit)}</td>
                <td className={actualProfit - projectedProfit >= 0 ? "event-pnl__pos" : "event-pnl__neg"}>
                  {actualProfit - projectedProfit >= 0 ? "+" : "−"}
                  {formatINR(Math.abs(actualProfit - projectedProfit))}
                </td>
              </tr>
              <tr>
                <td>Cost</td>
                <td colSpan={2}>{formatINR(totalCost)}</td>
                <td>—</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="eventos-card event-pnl__tiers">
          <div className="eventos-label">Ticket tier split</div>
          <div className="eventos-subtle event-pnl__sub">By revenue</div>
          <TicketTierSplitPie tiers={data.chart_data.revenue_by_tier ?? []} />
        </div>

        <div className="eventos-card event-pnl__scenario">
          <div className="eventos-label">Scenario forecast</div>
          <ScenarioForecastPanel
            points={curveQuery.data?.points ?? []}
            loading={curveQuery.isPending}
            forecastModel={curveQuery.data?.forecast_model}
          />
        </div>

        <div className="eventos-card event-pnl__expense">
          <div className="eventos-label">Expense analysis</div>
          <CostBreakdownChart rows={costRows} variant="bars" />
        </div>
      </div>
    </div>
  );
};

export default EventPnlPage;
